import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import { useMatchDirtyGuard } from '@/context/MatchRuntimeContext';
import { getV2Error } from '@/services/api/v2';
import { MatchSection, MatchNote, matchKeys, matchesV2API } from '@/services/api/matchesV2';
import SectionQuery from '../detail/SectionQuery';

const NotesSection: React.FC<{ workspaceId: string; id: string; canCreate: boolean; reviewExists: boolean }> = ({ workspaceId, id, canCreate, reviewExists }) => {
  const client = useQueryClient();
  const [body, setBody] = useState('');
  useMatchDirtyGuard(`match-note-${id}`, Boolean(body), 'note draft');
  const key = matchKeys.section(workspaceId, 'manual', id, 'notes');
  const query = useQuery({ queryKey: key, queryFn: () => matchesV2API.notes(workspaceId, id) });
  const add = useMutation({
    mutationFn: () => matchesV2API.addNote(workspaceId, id, body.trim()),
    onSuccess: (note: MatchNote) => {
      client.setQueryData<MatchSection<MatchNote[]>>(key, (current: MatchSection<MatchNote[]> | undefined) => ({ data: [note, ...(current?.data || [])], capabilities: current?.capabilities }));
      setBody('');
    },
  });
  const writable = canCreate && reviewExists;
  return <SectionQuery title="Notes" query={query}><div className="space-y-4">
    <ul className="space-y-3">{query.data?.data.map((note: MatchNote) => <li key={note.id} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800"><p className="text-sm font-semibold">{note.author.name} <time className="font-normal text-gray-500" dateTime={note.createdAt}>{new Date(note.createdAt).toLocaleString()}</time></p><p className="mt-2 whitespace-pre-wrap">{note.body}</p></li>)}</ul>
    {query.data?.data.length === 0 && <p>No notes.</p>}
    {canCreate && !reviewExists && <p className="rounded-lg bg-amber-50 p-3 text-amber-900 dark:bg-amber-950 dark:text-amber-100">Save the match review before adding notes.</p>}
    {writable && <div><label className="text-sm font-medium">Add note<textarea className="mt-1 min-h-28 w-full rounded-md border bg-white p-3 dark:border-gray-700 dark:bg-gray-900" maxLength={4000} value={body} onChange={event => setBody(event.target.value)} /></label><Button className="mt-2" loading={add.isPending} loadingLabel="Adding note" disabled={!body.trim()} onClick={() => add.mutate()}>Add note</Button></div>}
    {add.isError && <p role="alert" className="text-red-700 dark:text-red-300">{getV2Error(add.error).message}</p>}
  </div></SectionQuery>;
};
export default NotesSection;
